import React, { useState, useRef, useCallback, useEffect, memo, useMemo } from 'react';
import type { FC } from 'react';
import { css, keyframes } from '@compiled/react';

import { reduceMotionAsPerUserPreference } from '@atlaskit/motion';
import { token } from '@atlaskit/tokens';

import {
	expValEquals,
	UNSAFE_expValNoExposure,
	UNSAFE_expValEqualsNoExposure,
} from '@confluence/feature-experiments';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { getEmojiValueFromContent } from '@confluence/emoji-title/entry-points/transformer.ts';
import { useElementSeenCallback } from '@confluence/analytics/entry-points/useElementSeenCallback.ts';
import { LivePagePreloader } from '@confluence/live-page-preloader/entry-points/LivePagePreloader.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';

import {
	getContentLink,
	getSpaceLink,
	getUserAccountId,
	getLinkForSummaryLinkUpdate,
	isSupportedEvent,
} from '../feedCard-helpers.ts';
import { AllActivityDropdown, ActivityLineUpdateLink } from '../ActivityLine/index.ts';
import { ActionTime } from '../ActionTime.tsx';
import { useFeedCardAnalyticsEvents } from '../useFeedCardAnalyticsEvents.ts';
import { useIsMounted } from '../useIsMounted.ts';
import type { FollowingFeedSortByValue } from '../../Feed.tsx';
import { FeedEventType, FeedItemSourceType } from '../../feedCardEnums.ts';
import type { FeedEventComment, FeedItem, SummaryLineUpdate } from '../../feedCardTypes.ts';

import { FeedCardContext } from './FeedCardContext.ts';
import { FeedCardBody } from './FeedCardBody.tsx';
import { FeedCardInvisibleFullSizedLink } from './FeedCardInvisibleFullSizedLink.tsx';

type SummaryLineCommentUpdate = SummaryLineUpdate & {
	readonly type: 'COMMENT';
	readonly comment: NonNullable<SummaryLineUpdate['comment']>;
};

const simpleWrapperStyle = css({
	display: 'flex',
	flexDirection: 'row',
	background: token('elevation.surface.raised'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	padding: token('space.200'),
	marginBottom: token('space.150'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& > div': {
		width: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& a': {
		textDecoration: 'underline',
		'&:hover': {
			textDecoration: 'none',
		},
		'&:focus': {
			textDecoration: 'underline',
		},
	},
});

export const SimpleWrapper = (props) => <div css={simpleWrapperStyle} {...props} />;

const wrapperStyle = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'row',
	background: token('elevation.surface.raised'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.large'),
	'&:hover': {
		boxShadow: token('elevation.shadow.raised'),
	},
	padding: token('space.200'),
	marginBottom: token('space.150'),
	'--show-action': 0,
	'&:hover, &:active, &:focus-within': {
		cursor: 'pointer',
		'--show-action': 1,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage
	...reduceMotionAsPerUserPreference,
});

const wrapperExperimentPersistentStyles = css({
	'--show-action': 1,
	'&:hover, &:active, &:focus-within': {
		cursor: 'pointer',
	},
});

const actionMenuStyle = css({
	'--show-action': 1,
});

const fadeIn = keyframes({
	'0%': {
		opacity: 0.5,
	},
	'100%': {
		opacity: 1,
	},
});

const fadeInAnimation = css({
	animation: `${fadeIn} 200ms ease-in-out`,
});

// Note: this component extracts the props that we don't want to send to the DOM
// This avoids styled errors about these props being passed to the DOM
export const Wrapper = ({ isActionMenuOpen, disableAnimation, ...rest }) => (
	<div
		css={[
			wrapperStyle,
			isActionMenuOpen && actionMenuStyle,
			!disableAnimation && fadeInAnimation,
			expValEquals('confluence_feed_card_icons_persistent', 'isEnabled', true) &&
				wrapperExperimentPersistentStyles,
		]}
		{...rest}
	/>
);

const avatarWrapperStyle = css({
	paddingRight: token('space.200'),
});

const contentWrapperStyle = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1',
	minWidth: '0',
});

const summaryLineStyle = css({
	display: 'flex',
	alignItems: 'center',
	marginBottom: token('space.150'),
});

const summaryLineRelevantActionStyle = css({
	display: 'flex',
	flexDirection: 'row',
	color: token('color.text.subtle'),
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
	alignItems: 'center',

	flexGrow: 1,

	// handle the active state outline with overflow: hidden
	padding: token('space.050'),
	margin: token('space.negative.050'),
	marginRight: token('space.050'), // padding + margin should be 8px
});

const summaryLineActionsStyle = css({
	color: token('color.text.subtle'),
	display: 'flex',
	alignItems: 'center',
	flexShrink: 0,
});

const spacerStyle = css({
	margin: `0 ${token('space.050')}`,
});

type FeedCardProps = {
	feedItem: FeedItem;
	ranking: number;
	disableAnimation?: boolean;
	shouldHideComments?: boolean;
	followingFeedSortBy?: FollowingFeedSortByValue;
};

export const FeedCard: FC<FeedCardProps> = memo(
	({ feedItem, ranking, disableAnimation, shouldHideComments, followingFeedSortBy }) => {
		const isMounted = useIsMounted();
		const [hasBeenHovered, setHasBeenHovered] = useState(false);
		const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
		const bottomOfCardRef = useRef<HTMLDivElement | null>(null);

		const mainUpdate = feedItem.summaryLineUpdate;
		const recentActionsCount = feedItem.recentActionsCount;
		const mainUpdateUserId = getUserAccountId(mainUpdate.user);
		const updateType = mainUpdate?.type;
		const contentUrl = getContentLink(feedItem.content);
		const updateTime = mainUpdate?.datetime;
		const contentId = feedItem?.content?.id || '';
		const updateLink = getLinkForSummaryLinkUpdate(mainUpdate, contentUrl, contentId);
		const contentType = feedItem?.content?.type || 'page';
		const contentSubType = feedItem?.content?.subType;
		const contentTitle = feedItem?.content?.title || '';
		const contentExcerpt = feedItem.content?.excerpt;
		const aiSnippet = feedItem?.content?.aiProperty?.objectData;
		const authorName = feedItem?.content?.history?.createdBy?.displayName || '';
		const ownerName = feedItem?.content?.history?.ownedBy?.displayName || '';
		const spaceId = feedItem?.content?.space?.id || '';
		const spaceKey = feedItem?.content?.space?.key || undefined;
		const spaceName = feedItem?.content?.space?.name || '';
		const lastModifiedDate = feedItem.content?.metadata?.lastModifiedDate || '';
		const emoji = getEmojiValueFromContent(feedItem.content);
		const numComments = feedItem?.content?.metadata?.comments?.commentsCount || 0;
		const spaceUrl = getSpaceLink(feedItem.content);
		const authorId = getUserAccountId(feedItem.content?.history?.createdBy) || '';
		const ownerId = getUserAccountId(feedItem.content?.history?.ownedBy) || '';
		const hasNullComment =
			updateType === FeedEventType.COMMENT &&
			(mainUpdate as SummaryLineCommentUpdate).comment === null;
		const supportedEvent = isSupportedEvent(feedItem.summaryLineUpdate.type);
		const isLivePage = contentType === 'page' && contentSubType === 'live';

		const hasAiSnippet =
			aiSnippet &&
			expValEquals('confluence_ai_snippets_in_following_popular_feeds', 'isEnabled', true);

		const summaryContent = hasAiSnippet ? aiSnippet : contentExcerpt;

		const sourceIds = feedItem.source.reduce<string[]>((filtered, source) => {
			if (source === FeedItemSourceType.PERSON && mainUpdateUserId) {
				filtered.push(mainUpdateUserId);
			} else if (source === FeedItemSourceType.SPACE && spaceId) {
				filtered.push(spaceId);
			}
			return filtered;
		}, []);

		const handleMouseEnter = React.useCallback(() => {
			setHasBeenHovered(true);
		}, [setHasBeenHovered]);

		const fireAnalyticsEvent = useFeedCardAnalyticsEvents(contentId, spaceId, 'following', {
			ranking,
			contentType,
			updateType,
			sourceIds,
			isFromSource: feedItem.source,
			recentActionsCount,
			commentsCount: numComments,
			lastUpdated: new Date(mainUpdate.datetime).getTime(),
		});

		const onObserved = useCallback(() => {
			fireAnalyticsEvent({
				type: 'sendTrackEvent',
				action: 'observed',
				actionSubject: 'feedCard',
			});
		}, [fireAnalyticsEvent]);

		useElementSeenCallback(bottomOfCardRef, onObserved);

		useEffect(() => {
			if (hasNullComment) {
				fireAnalyticsEvent({
					type: 'sendOperationalEvent',
					action: 'hidden',
					actionSubject: 'nullCommentFeedCard',
				});
			}
		}, [hasNullComment, fireAnalyticsEvent]);

		const feedCardContextValue = useMemo(() => ({ fireAnalyticsEvent }), [fireAnalyticsEvent]);

		if (hasNullComment || !supportedEvent) {
			return null;
		}

		const cardHref =
			shouldHideComments && updateType === FeedEventType.COMMENT ? updateLink : contentUrl;

		return (
			<FeedCardContext.Provider value={feedCardContextValue}>
				<LivePagePreloader isLivePage={isLivePage}>
					<HoverPreloader
						name="feed-card"
						preloadUrl={cardHref}
						preloadOptions={PreloadingPresets.onlyJS}
					>
						<Wrapper
							data-testid="feed-card"
							isActionMenuOpen={isActionMenuOpen}
							disableAnimation={isMounted || disableAnimation}
							onMouseEnter={handleMouseEnter}
							onFocus={handleMouseEnter}
						>
							<FeedCardInvisibleFullSizedLink
								href={cardHref}
								contentType={contentType}
								followingFeedSortBy={followingFeedSortBy}
								hasAiSnippet={!!hasAiSnippet}
							/>
							{/* eslint-disable-next-line @atlaskit/design-system/use-primitives */}
							<div css={avatarWrapperStyle}>
								<ProfileAvatar
									userId={mainUpdateUserId}
									src={mainUpdate.user?.profilePicture?.path || ''}
									canViewProfile
									size="medium"
								/>
							</div>
							<div css={contentWrapperStyle}>
								<div css={summaryLineStyle}>
									<div css={summaryLineRelevantActionStyle}>
										<ActivityLineUpdateLink
											type={updateType}
											userDisplayName={mainUpdate.user?.displayName || null}
											link={updateLink}
											groupSize={mainUpdate.groupSize}
											activityDropdownLineItem={false}
										/>
										<div css={spacerStyle}>•</div>
										<ActionTime actionTime={updateTime} />
									</div>

									<div css={summaryLineActionsStyle}>
										{(recentActionsCount > 1 ||
											UNSAFE_expValEqualsNoExposure(
												'cc_analytics_use_topn_query_in_feed_experiment',
												'cohort',
												'test',
											)) && (
											<AllActivityDropdown
												contentUrl={contentUrl}
												contentId={contentId}
												recentActionsCount={recentActionsCount}
												shouldShowRecentActionsCountNumber={
													UNSAFE_expValNoExposure(
														'cc_analytics_use_topn_query_in_feed_experiment',
														'cohort',
														'control',
													) === 'control'
												}
											/>
										)}
									</div>
								</div>
								<FeedCardBody
									toggleActionMenu={setIsActionMenuOpen}
									contentId={contentId}
									emoji={emoji}
									contentTitle={contentTitle}
									contentType={contentType}
									contentSubType={contentSubType}
									contentUrl={contentUrl}
									spaceId={spaceId}
									spaceKey={spaceKey}
									spaceName={spaceName}
									spaceUrl={spaceUrl}
									authorName={authorName}
									authorId={authorId}
									ownerName={ownerName}
									ownerId={ownerId}
									contentExcerpt={summaryContent}
									numComments={numComments}
									lastModifiedDate={lastModifiedDate}
									comment={
										updateType === FeedEventType.COMMENT
											? (mainUpdate as FeedEventComment)
											: undefined
									}
									hasBeenHovered={hasBeenHovered}
									source="following-feed"
									shouldHideComments={shouldHideComments}
									followingFeedSortby={followingFeedSortBy}
								/>
								<div ref={bottomOfCardRef} />
							</div>
						</Wrapper>
					</HoverPreloader>
				</LivePagePreloader>
			</FeedCardContext.Provider>
		);
	},
);
